import { DATE_MASK_FORMAT } from '@givebutter/util';
import { RelativeDateRangeOperator } from 'components/filter/types';
import { capitalize } from 'lodash';
import { DateTime, Settings } from 'luxon';

const DEFAULT_DATE_FORMAT = 'MM/dd/yy';
const SHORT_DATE_FORMAT = 'yyyy-MM-dd';
const LONG_DATE_FORMAT = 'MMMM d, yyyy';
const formatDate = (date: DateTime, format = DEFAULT_DATE_FORMAT) => {
    if (!date.isValid) {
        throw new Error('Invalid JS date: ' + date.toJSON());
    }

    return date.toFormat(format);
};

const getMinDate = () => DateTime.now().set({ hour: 0, minute: 0 });

const isoDateFromServer = (date: string) =>
    DateTime.fromISO(date, { zone: 'UTC' }).setZone(Settings.defaultZone);

/**
 * Parses an SQL datetime and keeps it in UTC.
 */
const rawDateFromServer = (date: string) => DateTime.fromSQL(date, { zone: 'UTC' });

/**
 * Parses an SQL datetime and shifts it to the `defaultZone` (account timezone).
 */
const dateFromServer = (date: string) => rawDateFromServer(date).setZone(Settings.defaultZone);

const dateOfBirthFromServer = (dob: string) => {
    const fullDob = DateTime.fromFormat(dob ?? '', SHORT_DATE_FORMAT);
    /*
        Luxon parses leap days as invalid when not in a leap year, and if a year is not provided,
        it defaults to current year, which may or may not be a leap year.
        
        To ensure we accept leap days as valid DOB, we need to provide a valid leap year.
    */
    const shortDob = DateTime.fromFormat(dob ? `2000-${dob}` : '', SHORT_DATE_FORMAT);

    return {
        fullDob,
        shortDob,
    };
};

const dateOfBirthToFormat = (dob: string, formats: { long: string; short: string }) => {
    const { fullDob, shortDob } = dateOfBirthFromServer(dob);

    if (fullDob.isValid) {
        return fullDob.toFormat(formats.long);
    } else if (shortDob.isValid) {
        return shortDob.toFormat(formats.short);
    }

    return '';
};

const toServer = (date: DateTime) => date.setZone('utc');
const dateToServerDateTime = (date: DateTime) => toServer(date).toISO();

const maskedDateToServerDateTime = (date: string, mask = DATE_MASK_FORMAT) => {
    const { minute, hour, second, millisecond } = DateTime.now();

    return dateToServerDateTime(
        DateTime.fromFormat(date, mask).set({
            minute,
            hour,
            second,
            millisecond,
        }),
    );
};

const getClosestThirtyMinuteDateTime = () => {
    const now = DateTime.now();
    const minutes = now.minute;
    const roundedMinutes = Math.round(minutes / 30) * 30;

    return now.set({ minute: roundedMinutes });
};

/**
 * Try multiple parsing formats and return it if valid. Otherwise false.
 */
const firstValidDate = (
    value: string,
    { allowTime = true, timezone }: { allowTime?: boolean; timezone?: string },
): DateTime | false => {
    let date = DateTime.fromFormat(value, 'LL/dd/yyyy', { zone: timezone });
    if (date.isValid) {
        return date;
    }

    date = DateTime.fromFormat(value, 'yyyy-LL-dd', { zone: timezone });
    if (date.isValid) {
        return date;
    }

    // Didn't get a valid date, so short-circuit before
    // we try to parse time-allowing formats.
    if (!allowTime) {
        return false;
    }

    date = DateTime.fromSQL(value, { zone: timezone });
    if (date.isValid) {
        return date;
    }

    date = DateTime.fromISO(value, { zone: timezone });
    if (date.isValid) {
        return date;
    }

    return false;
};

const shortDateToDateTime = (date: string) => DateTime.fromFormat(date, SHORT_DATE_FORMAT);

const toNearestFifteenMinuteDateTime = (date: DateTime) => {
    const minutes = date.minute;
    const roundedMinutes = Math.round(minutes / 15) * 15;

    return date.set({ minute: roundedMinutes });
};

const isCurrentYear = (date: DateTime) => {
    return date.year === DateTime.now().year;
};

const transformDateRangeTypeToReadable = (dateRangeType: RelativeDateRangeOperator) => {
    switch (dateRangeType) {
        case 'LAST-WEEK':
            return 'Last 7 days';
        case 'LAST-TWO-WEEKS':
            return 'Last 14 days';
        case 'LAST-MONTH':
            return 'Last 4 weeks';
        case 'LAST-QUARTER':
            return 'Last 3 months';
        case 'LAST-YEAR':
            return 'Last 12 months';
        case 'MTD':
            return 'Month to date';
        case 'QTD':
            return 'Quarter to date';
        case 'YTD':
            return 'Year to date';
        case 'ALL-TIME':
            return 'All time';
        case 'TODAY':
        case 'YESTERDAY':
        case 'CUSTOM':
            return capitalize(dateRangeType);
    }
};

export {
    DEFAULT_DATE_FORMAT,
    SHORT_DATE_FORMAT,
    LONG_DATE_FORMAT,
    //
    getMinDate,
    rawDateFromServer,
    dateFromServer,
    isoDateFromServer,
    toServer,
    dateToServerDateTime,
    formatDate,
    getClosestThirtyMinuteDateTime,
    toNearestFifteenMinuteDateTime,
    isCurrentYear,
    maskedDateToServerDateTime,
    firstValidDate,
    transformDateRangeTypeToReadable,
    shortDateToDateTime,
    dateOfBirthFromServer,
    dateOfBirthToFormat,
};
